import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { BookOpen, Search, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { AuthRequired } from "../../components/AuthRequired";
import { PageLayout } from "../../components/PageLayout";
import { StoriesContent } from "../../components/stories/StoriesContent";
import { StoriesEmptyState } from "../../components/stories/StoriesEmptyState";
import { StoriesErrorState } from "../../components/stories/StoriesErrorState";
import { StoriesLoadingState } from "../../components/stories/StoriesLoadingState";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { ConfirmationModal } from "../../components/ui/confirmation-modal";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigation } from "../../contexts/NavigationContext";
import { apiClient } from "../../lib/api";

export const Route = createFileRoute("/_layout/my-stories")({
    component: MyStories,
});

function MyStories() {
    const { user, loading } = useAuth();
    const { navigateToStory, navigateToStoryMode } = useNavigation();
    const queryClient = useQueryClient();

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [sortBy, setSortBy] = useState<"updated_at" | "topic">("updated_at");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [storyToDelete, setStoryToDelete] = useState<{ id: string; title?: string; topic: string } | null>(null);

    const {
        data: storiesData,
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: ["user-stories-paginated", page, limit, sortBy, sortOrder, search],
        queryFn: () => apiClient.getUserStoriesPaginated(page, limit, sortBy, sortOrder, search),
        enabled: !!user,
    });

    const deleteStoryMutation = useMutation({
        mutationFn: (storyId: string) => apiClient.deleteStory(storyId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["user-stories-paginated"] });
            setDeleteModalOpen(false);
            setStoryToDelete(null);
        },
        onError: (error) => {
            console.error("Failed to delete story:", error);
            alert("Failed to delete story. Please try again.");
        },
    });

    const stories = storiesData?.stories || [];
    const totalPages = storiesData?.pagination?.totalPages || 0;
    const total = storiesData?.pagination?.total || 0;

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSearch(searchInput);
        setPage(1);
    };

    const handleSortChange = (newSortBy: "updated_at" | "topic") => {
        if (newSortBy === sortBy) {
            setSortOrder(sortOrder === "desc" ? "asc" : "desc");
        } else {
            setSortBy(newSortBy);
            setSortOrder(newSortBy === "updated_at" ? "desc" : "asc");
        }
        setPage(1);
    };

    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    };

    const handleDeleteClick = (story: { id: string; title?: string; topic: string }) => {
        setStoryToDelete(story);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (storyToDelete) {
            deleteStoryMutation.mutate(storyToDelete.id);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false);
        setStoryToDelete(null);
    };

    const handleClearSearch = () => {
        setSearch("");
        setSearchInput("");
    };

    const renderStories = () => {
        if (isLoading) {
            return <StoriesLoadingState limit={limit} />;
        }

        if (error) {
            return <StoriesErrorState onRetry={refetch} />;
        }

        if (stories.length === 0) {
            return <StoriesEmptyState search={search} onClearSearch={handleClearSearch} />;
        }

        return (
            <StoriesContent
                stories={stories}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                onStoryClick={navigateToStory}
                onDelete={handleDeleteClick}
                page={page}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
            />
        );
    };

    // Show loading state while auth is being determined
    if (loading) {
        return (
            <PageLayout title="My Stories" breadcrumbs={[{ label: "Home", to: "/" }, { label: "My Stories" }]}>
                <div className="flex items-center justify-center min-h-96">
                    <div className="p-8 max-w-md">
                        <div className="text-center space-y-4">
                            <div className="h-12 w-12 bg-muted animate-pulse rounded mx-auto"></div>
                            <div className="h-8 w-32 bg-muted animate-pulse rounded mx-auto"></div>
                            <div className="h-4 w-48 bg-muted animate-pulse rounded mx-auto"></div>
                            <div className="h-10 w-24 bg-muted animate-pulse rounded mx-auto"></div>
                        </div>
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (!user) {
        return (
            <PageLayout title="My Stories" breadcrumbs={[{ label: "Home", to: "/" }, { label: "My Stories" }]}>
                <AuthRequired
                    icon={<BookOpen className="h-12 w-12 text-primary mx-auto" />}
                    description="Please sign in to view your stories!"
                />
            </PageLayout>
        );
    }

    return (
        <PageLayout title="My Stories" breadcrumbs={[{ label: "Home", to: "/" }, { label: "My Stories" }]}>
            <main className="container mx-auto sm:px-6">
                <div className="max-w-6xl mx-auto space-y-6">
                    <Card className="p-3 md:p-6 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
                        <div className="flex items-center gap-3 md:gap-6">
                            <div className="w-8 h-8 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                <Sparkles className="h-4 w-4 md:h-6 md:w-6 text-primary" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-base md:text-xl font-semibold text-primary">Ready for more?</h2>
                                <p className="hidden md:block text-muted-foreground">
                                    Create a fresh story and let your drawings guide the narrative!
                                </p>
                            </div>
                            <Button size="sm" className="md:size-lg flex-shrink-0 gap-2" onClick={navigateToStoryMode}>
                                <Sparkles className="h-4 w-4" />
                                <span className="hidden sm:inline">Start New Story</span>
                                <span className="sm:hidden">New Story</span>
                            </Button>
                        </div>
                    </Card>

                    <Card className="p-6">
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <form onSubmit={handleSearchSubmit} className="w-full md:flex-1">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary h-4 w-4" />
                                    <Input
                                        type="text"
                                        placeholder="Search stories..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        className="pl-10 pr-10"
                                    />
                                    {searchInput && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchInput("");
                                                setSearch("");
                                                setPage(1);
                                            }}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-primary"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            </form>

                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Show:</span>
                                    <Select
                                        value={limit.toString()}
                                        onValueChange={(value: string) => handleLimitChange(Number(value))}
                                    >
                                        <SelectTrigger className="w-20 h-8">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="5">5</SelectItem>
                                            <SelectItem value="10">10</SelectItem>
                                            <SelectItem value="20">20</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">Sort:</span>
                                    <Select
                                        value={`${sortBy}-${sortOrder}`}
                                        onValueChange={(value: string) => {
                                            const [newSortBy, newSortOrder] = value.split("-") as [
                                                "updated_at" | "topic",
                                                "asc" | "desc",
                                            ];
                                            setSortBy(newSortBy);
                                            setSortOrder(newSortOrder);
                                            setPage(1);
                                        }}
                                    >
                                        <SelectTrigger className="w-32 h-8">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="updated_at-desc">Newest First</SelectItem>
                                            <SelectItem value="updated_at-asc">Oldest First</SelectItem>
                                            <SelectItem value="topic-asc">A-Z</SelectItem>
                                            <SelectItem value="topic-desc">Z-A</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {renderStories()}
                </div>
            </main>

            <ConfirmationModal
                isOpen={deleteModalOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title="Delete Story"
                description={`Are you sure you want to delete "${storyToDelete?.title || storyToDelete?.topic}"? This action cannot be undone.`}
                variant="destructive"
                confirmButtonText="Delete"
                isLoading={deleteStoryMutation.isPending}
                loadingText="Deleting..."
            />
        </PageLayout>
    );
}
